import {
  Directive,
  HostListener,
  EnvironmentInjector,
  OnInit,
  OnDestroy,
  Input,
  ElementRef,
  Renderer2,
  AfterViewInit,
  ViewChild,
  inject,
  computed,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { getNoteDisplayTitle, isNoteDisplayTitleDerived } from '../../_util/note-display-title.util';
import { Location } from '@angular/common';
import { DatePipe } from '@angular/common';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { catchError, takeUntil } from 'rxjs/operators';
import { of, firstValueFrom, Subject } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Note, DataService, Tag, SimilarNoteResult, AiSummary } from '../../_service/data.service';
//import { OLD__UserSettingsService } from '../../_service/OLD_usersettings.service';
import { FilterParams, FilterService } from '../../_service/filter.service';
// import { PrintService } from '../../_service/print.service';
import { SnackbarService } from '../../_service/snackbar.service';
import { NavigationService } from '../../_service/navigation.service';
import { LayoutService } from '../../_service/layout.service';
import { UserSettingsService } from '../../_service/user-settings.service';
import { EnvironmentService } from '../../_service/sync/environment.service';
import { SyncCoordinatorService } from '../../_service/sync/sync-coordinator.service';
import { SpaceDirectoryService } from '../../_service/space-directory.service';
import { ScopedTagsService } from '../../_service/scoped-tags.service';
import { filterPrivateTags, resolveNoteSpaceTags } from '../../_util/tag-scope.util';
import { ConnectivityDisplay, resolveConnectivityDisplay } from '../home/logic/sync-state.logic';
import { AiSessionService } from '../../_service/ai-session.service';
import { MenuItem, ConfirmationService } from 'primeng/api';
import hljs from 'highlight.js/lib/core';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { FileUploadComponent } from '../file-upload/file-upload.component';
import { RichEditorComponent } from '../rich-editor/rich-editor.component';
import { AlertService, AlertType } from '../../_service/alert.service'; // Adjust path as needed
import { NotePrintExportService } from '../../_service/note-print-export.service';
import { NoteActionsService } from '../../_service/note-actions.service';
import { formatPersistedNoteSize } from '../../_util/note-size.util';
import { NoteSizeService } from '../../_service/note-size.service';
import { getOrderedRelatedNoteIds } from '../../_util/related-notes.util';
import {
  BulkActionConfig,
  BulkActionParams,
  BulkActionRequest,
} from '../home/bulk-action-dialog/bulk-action-dialog.component';

// Import only the languages you need (optional but recommended for bundle size)
import typescript from 'highlight.js/lib/languages/typescript';
import javascript from 'highlight.js/lib/languages/javascript';
import python from 'highlight.js/lib/languages/python';
import xml from 'highlight.js/lib/languages/xml'; // for HTML/XML
import css from 'highlight.js/lib/languages/css'; // for HTML/XML
import bash from 'highlight.js/lib/languages/bash'; //
import json from 'highlight.js/lib/languages/json'; //
import {
  isActionRequired,
  isEmphasizedStatus,
  isLegacyStatus,
  isMutedStatus,
  isStruckStatus,
  legacyStatusHint,
  normalizePriority,
  priorityIndicator,
} from '../../_util/note-status.util';

hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('python', python);
hljs.registerLanguage('html', xml);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('json', json);
hljs.registerLanguage('css', css);
hljs.registerLanguage('bash', bash);

interface NewNoteResponse {
  message: string;
  id: string; // Changed from number to string as per your requirement
}

/**
 * Shared view-mode logic for the View Note page, NoteViewComponent (`/view/:_id`), which extends this
 * class and supplies its own template, styles, imports and providers (component metadata isn't
 * inherited).
 * @Directive() (no selector) lets Angular apply this base class's constructor DI, queries and host
 * listeners to the subclass.
 */
@Directive()
export abstract class NoteViewBase implements OnInit, OnDestroy {
  @Input() note!: Note;
  @ViewChild('trigger', { read: ElementRef }) triggerRef!: ElementRef<HTMLElement>;
  @ViewChild('zoomPanel', { read: ElementRef }) zoomPanelRef!: ElementRef<HTMLElement>;
  @ViewChild('descriptionEditor') descriptionEditor!: RichEditorComponent;

  showZoomPanel = false;
  zoomPanelStyle: { top?: string; left?: string } = {};
  isOnline: boolean = true; // Tracks online/offline status from EnvironmentService
  // Whether the server actually responded to the last sync attempt — distinct from
  // isOnline, which only reflects the browser's network interface. See SyncStateLogic.
  serverReachable: boolean = true;
  get connectivity(): ConnectivityDisplay {
    return resolveConnectivityDisplay(this.isOnline, this.serverReachable);
  }
  private destroy$ = new Subject<void>();
  @HostListener('document:keydown.escape') onEsc() {
    console.log('ESC pressed - hide overlay if open');
    if (this.showZoomPanel) {
      this.showZoomPanel = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocClick(ev: MouseEvent) {
    // Use composedPath for reliable hit-testing (Shadow DOM / portals / appendTo="body")
    const path = (ev.composedPath?.() || []) as EventTarget[];

    const clickedTrigger = this.triggerRef?.nativeElement ? path.includes(this.triggerRef.nativeElement) : false;

    const clickedInsidePanel = this.zoomPanelRef?.nativeElement
      ? path.includes(this.zoomPanelRef.nativeElement)
      : false;

    if (!clickedTrigger && !clickedInsidePanel && this.showZoomPanel) {
      this.showZoomPanel = false;
    }
  }

  filter: any;
  homeState: { view?: string; calendarMonth?: string } | null = null;
  editorContent: string = '';
  isHeaderVisible: boolean = false;
  isMetadataMoreVisible: boolean = false;
  errorMessage: string = '';
  loadingCounter = 0; // initially count of loading objects in progress
  // Collapsible section states
  isDescriptionExpanded: boolean = true;
  isFileAttachmentsExpanded: boolean = false;
  isTagsExpanded: boolean = false;
  isKeywordsExpanded: boolean = false;
  isCommentsExpanded: boolean = false;
  isRelatedNotesExpanded: boolean = false;
  relatedNotes: string[] = [];
  areAllCollapsibleExpanded: boolean = true;
  isFavorite: boolean = false;

  /** Display-only title: falls back to the first content line when the note has no Title. */
  get displayTitle(): string {
    return getNoteDisplayTitle(this.note?.Title, this.note?.DescriptionHtml);
  }

  get isDisplayTitleDerived(): boolean {
    return isNoteDisplayTitleDerived(this.note?.Title);
  }
  favoriteNoteIds: string[] = [];

  MobileViewMenuIsVisible: boolean = false;
  availableTags: Tag[] = [];
  /** The note's SPACE tags (Note.Tags) — tasks/frontend-tagging.md. */
  noteTags: Tag[] = [];
  /** The current user's PRIVATE tags on this note (private-tag-assignment API, never Note.Tags). */
  privateNoteTags: Tag[] = [];
  private privateTagIds: string[] = [];
  /** SPACE tags of this note's Space (the user's own tag list only covers their own database). */
  private spaceTagCatalog: Tag[] = [];
  private readonly scopedTags = inject(ScopedTagsService);
  private readonly noteActions = inject(NoteActionsService);
  menuItems: MenuItem[] = [];
  tempMessage: string = '';
  layoutService: LayoutService = inject(LayoutService);
  isDarkMode = computed(() => this.layoutService.appState().darkMode);
  editorZoomFactor: number = 150;
  private markupCache = new Map<string, string>();
  showRawMarkup = false;
  viewDocxExportDialogVisible = false;
  similarNotesDialogVisible = false;
  similarNotesLoading = false;
  similarNotesResults: SimilarNoteResult[] = [];
  similarNotesError = '';
  readonly viewDocxExportAction: BulkActionConfig = NoteActionsService.DOCX_EXPORT_ACTION;

  private readonly noteSizes = inject(NoteSizeService);

  /** Note + all its attachments (see NoteSizeService). */
  get noteSizeLabel(): string {
    return formatPersistedNoteSize(this.noteSizes.totalSizeBytes(this.note));
  }

  /** Set from FileUploadComponent's (countChange) once it resolves the note's real attachment
   * list (which can come from the backend/IndexedDB independently of note.Attachments — see
   * FileUploadComponent.loadAttachments()). Preferred over the note.Attachments-based estimate
   * below once known, since that field can be empty/stale even when attachments do exist for this
   * note (a real note:<id> was found with this exact symptom: 1 attachment on the backend, but an
   * empty Attachments array on the note document itself). */
  resolvedAttachmentCount: number | null = null;

  get attachmentCount(): number {
    if (this.resolvedAttachmentCount !== null) {
      return this.resolvedAttachmentCount;
    }
    const attachments = this.note?.Attachments;
    return Array.isArray(attachments) ? attachments.length : 0;
  }

  get tagCount(): number {
    return (Array.isArray(this.noteTags) ? this.noteTags.length : 0) + this.privateNoteTags.length;
  }

  /** tasks/space_ux.md — the note's Space, by name (Technical details still shows the raw id
   * separately, as "Space ID"). */
  get spaceName(): string {
    return this.spaceDirectory.getSpaceName(this.note?.spaceId);
  }

  constructor(
    private route: ActivatedRoute,
    private data_service: DataService,
    private location: Location,
    private router: Router,
    private filterService: FilterService,
    private datePipe: DatePipe,
    private renderer: Renderer2,
    private elRef: ElementRef,
    //private userSettingsService: OLD__UserSettingsService,
    private userSettings: UserSettingsService,
    private injector: EnvironmentInjector,
    // private printService: PrintService,
    private snackbarService: SnackbarService,
    private titleService: Title,
    private navigationService: NavigationService,
    private alertService: AlertService,
    private notePrintExportService: NotePrintExportService,
    private environmentService: EnvironmentService,
    private syncCoordinator: SyncCoordinatorService,
    private aiSessionService: AiSessionService,
    private cdr: ChangeDetectorRef,
    private confirmationService: ConfirmationService,
    private spaceDirectory: SpaceDirectoryService,
  ) {}

  openAskNotes(): void {
    this.aiSessionService.openDialog({
      currentNote: {
        noteId: this.note?._id,
        title: this.note?.Title,
        descriptionHtml: this.note?.DescriptionHtml ?? '',
        commentsHtml: this.note?.CommentsHtml ?? '',
      },
    });
  }

  ngOnInit(): void {
    this.userSettings.userSettings$.subscribe((settings) => {
      console.log('[UserSettingsService] initialize(): loaded', settings);
      if (settings) {
        console.log('primaryColor from settings =', settings.primaryColor);
        console.log('editorZoomFactor from settings =', settings.editorZoomFactor);
        if (settings.primaryColor) {
          this.layoutService.appState.update((state: any) => ({
            ...state,
            primary: settings.primaryColor,
          }));
        }
        const zoom = settings.editorZoomFactor ?? this.editorZoomFactor;
        this.editorZoomFactor = zoom;
        document.documentElement.style.setProperty('--fontScaleFactor', `${zoom}%`);
        document.documentElement.style.setProperty('--fontScaleRatio', `${zoom / 100}`);
        // document.documentElement.style.setProperty('--systemZoom', `${settings.systemZoomFactor}%`);
        this.favoriteNoteIds = this.normalizeFavoriteNoteIds(settings.favoriteNoteIds);
        this.updateFavoriteState();
      }
    });
    this.userSettings.editorZoomFactor$.subscribe((factor) => {
      if (typeof factor === 'number' && !Number.isNaN(factor)) {
        this.editorZoomFactor = factor;
        document.documentElement.style.setProperty('--fontScaleFactor', `${factor}%`);
        document.documentElement.style.setProperty('--fontScaleRatio', `${factor / 100}`);
      }
    });

    // Subscribe to network status changes
    this.environmentService.online$
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        this.isOnline = status;
        this.updateAiMenuItems();
        this.cdr.markForCheck();
      });

    // Server reachability - distinct from browser online/offline (see SyncStateLogic):
    // the browser can report "online" while the app's server is unreachable.
    this.syncCoordinator.serverReachable$
      .pipe(takeUntil(this.destroy$))
      .subscribe((reachable) => {
        this.serverReachable = reachable;
        this.cdr.markForCheck();
      });

    this.loadingCounter = 0; // Start loading
    //this.userSettingsService.loadSavedTheme();
    //this.userSettingsService.setZoomFactor(151);
    //document.documentElement.style.setProperty('--fontScaleFactor', `151%`);
    const currentNavigation = this.router.getCurrentNavigation();
    // this.zoomFactor = this.userSettingsService.getZoomFactor();
    this.initializePageMenu();

    console.log('Registered languages:', hljs.listLanguages());
    //console.log('in view NgOnInit(); user zoom factor= ', this.userSettingsService.getZoomFactor());

    const navState = currentNavigation?.extras?.state ?? (history.state || {});
    this.homeState = navState['homeState'] ?? null;
    if (navState && navState['filter']) {
      this.filter = navState['filter'];
      console.log('in view NgOnInit-1(); filter = ', this.filter);
    } else {
      this.filter = this.filterService.getFilterParams();
      console.log('in view NgOnInit-2(); filter = ', this.filter);
    }

    const preloadedNote = navState['note'] as Note | undefined;
    if (preloadedNote?._id) {
      this.applyLoadedNote(preloadedNote);
    }

    console.log('View-component: About to load note - param _id =', String(this.route.snapshot.paramMap.get('_id')));
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  async copyNoteId(): Promise<void> {
    const id = this.note?._id;
    if (!id) {
      this.snackbarService.showErrorMessage('No ID to copy.');
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(id);
        this.snackbarService.showSuccessMessage('ID copied to clipboard.');
        return;
      }

      this.fallbackCopyText(id);
      this.snackbarService.showSuccessMessage('ID copied to clipboard.');
    } catch (error) {
      console.error('Failed to copy note ID', error);
      this.snackbarService.showErrorMessage('Unable to copy ID.');
    }
  }

  private fallbackCopyText(text: string): void {
    const doc = this.elRef.nativeElement.ownerDocument as Document;
    const textarea = this.renderer.createElement('textarea');
    this.renderer.setProperty(textarea, 'value', text);
    this.renderer.setStyle(textarea, 'position', 'fixed');
    this.renderer.setStyle(textarea, 'top', '-1000px');
    this.renderer.setStyle(textarea, 'left', '-1000px');
    this.renderer.setStyle(textarea, 'opacity', '0');
    this.renderer.appendChild(doc.body, textarea);
    textarea.select();
    doc.execCommand('copy');
    this.renderer.removeChild(doc.body, textarea);
  }

  renderMarkup(input?: string): string {
    const source = String(input ?? '').trim();
    if (!source) {
      return '';
    }

    const cached = this.markupCache.get(source);
    if (cached !== undefined) {
      return cached;
    }

    const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(source);
    const parsed = looksLikeHtml ? source : (marked.parse(source, { async: false, breaks: true, gfm: true }) as string);
    const sanitized = DOMPurify.sanitize(parsed, {
      USE_PROFILES: { html: true },
      ADD_ATTR: [
        'colwidth',
        'style',
        'width',
        'height',
        'data-keep-aspect-ratio',
        'data-image-source-type',
        'data-attachment-id',
        'data-original-url',
      ],
    });

    this.markupCache.set(source, sanitized);
    if (this.markupCache.size > 100) {
      this.markupCache.clear();
      this.markupCache.set(source, sanitized);
    }

    return sanitized;
  }

  private applyLoadedNote(note: Note): void {
    const normalizedNote = { ...note };

    if (!normalizedNote.ReferenceDate) {
      normalizedNote.ReferenceDate = normalizedNote.created_on;
    }
    if (normalizedNote.Confidential === undefined || normalizedNote.Confidential === null) {
      normalizedNote.Confidential = false;
    }

    // A different note: forget the previous note's live attachment count so the badge doesn't
    // briefly show it for the new note before FileUploadComponent re-resolves (it won't re-fetch,
    // and so won't re-emit, for the same note id — see its lastLoadedNoteId guard).
    if (this.note?._id !== normalizedNote._id) {
      this.resolvedAttachmentCount = null;
    }

    this.note = normalizedNote;
    console.log('✅ Loaded note:', normalizedNote);
    this.spaceTagCatalog = this.scopedTags.getCachedSpaceTags(normalizedNote.spaceId);
    this.privateTagIds = this.scopedTags.getCachedPrivateTagIds(normalizedNote._id) ?? [];
    this.updateFavoriteState();
    this.refreshDerivedNoteState();
    void this.loadScopedTags(normalizedNote);
  }

  /** Space tags of the note's Space and the user's private tags on it — cached values render first
   * (offline-safe), then the server's. */
  private async loadScopedTags(note: Note): Promise<void> {
    const [spaceTags, privateIds] = await Promise.all([
      firstValueFrom(this.scopedTags.loadSpaceTags(note.spaceId)),
      firstValueFrom(this.scopedTags.loadPrivateTagIds(note._id, note.spaceId ?? null)),
    ]);
    if (this.note?._id !== note._id) return;
    this.spaceTagCatalog = spaceTags;
    this.privateTagIds = privateIds;
    this.refreshDerivedNoteState();
  }

  private refreshDerivedNoteState(): void {
    if (!this.note) {
      return;
    }

    // Note.Tags holds the note's Space tags; the user's private tags come from their own private
    // assignment (privateTagIds), resolved against their private tag catalog.
    const privateCatalog = filterPrivateTags(this.availableTags);
    this.noteTags = resolveNoteSpaceTags(this.note.Tags, this.spaceTagCatalog, {
      privateTagIds: [...privateCatalog.map((tag) => tag._id), ...this.privateTagIds],
      spaceId: this.note.spaceId ?? null,
    });
    const privateById = new Map(privateCatalog.map((tag) => [tag._id, tag] as const));
    this.privateNoteTags = this.sortTagsByOrder(
      this.privateTagIds.map((id) => privateById.get(id)).filter((tag): tag is Tag => !!tag),
    );
    const rawRelated = Array.isArray((this.note as any).RelatedNotes)
      ? (this.note as any).RelatedNotes
      : Array.isArray((this.note as any).relatedNotes)
        ? (this.note as any).relatedNotes
        : [];
    this.relatedNotes = getOrderedRelatedNoteIds(rawRelated);

    if (this.note.Title) {
      this.setTitle(this.note.Title);
    }

    this.cdr.markForCheck();
  }

  loadData(): void {
    console.log('NoteViewBase: in loadData()');

    const noteId = String(this.route.snapshot.paramMap.get('_id'));
    // Always refresh from IndexedDB/API so view-only preloaded state does not hide related notes.
    const shouldFetchNote = !!noteId;

    if (shouldFetchNote) {
      this.loadingCounter++;
      this.data_service.getNote(noteId).subscribe({
        next: (noteResp) => {
          if (noteResp.status === 'success' && noteResp.data) {
            this.applyLoadedNote(noteResp.data);
            return;
          }

          const msg = noteResp.error?.message || 'Failed to load note.';
          console.warn('⚠️ loadData():', msg);
          this.alertService?.warn?.(msg, { life: 4000 });
        },
        error: (err) => {
          console.error('❌ NoteViewBase: note load failed:', err);
          this.alertService?.error?.('Unexpected system error while loading note.', { life: 5000 });
          this.loadingCounter--;
        },
        complete: () => {
          this.loadingCounter--;
        },
      });
    }

    // Render tags from the already-loaded cache instantly; refresh from the
    // server in the background (see refreshTagCatalogInBackground) rather
    // than blocking note rendering on a live network round-trip.
    this.availableTags = Array.isArray(this.data_service.allTags)
      ? this.sortTagsByOrder(this.data_service.allTags)
      : [];

    void this.refreshTagCatalogInBackground();
  }

  /** Refreshes the tag catalog from the server after the note has already
   * rendered from cache, so opening a note never blocks on a live tags fetch. */
  private async refreshTagCatalogInBackground(): Promise<void> {
    try {
      const tagsResp = await firstValueFrom(this.data_service.getAllTags());
      if (tagsResp.status === 'success' && tagsResp.data) {
        this.availableTags = this.sortTagsByOrder(tagsResp.data);
        this.refreshDerivedNoteState();
        this.cdr.markForCheck();
      }
    } catch (err) {
      console.warn('Failed to refresh tag catalog:', err);
    }
  }

  initializePageMenu() {
    this.menuItems = [
      {
        label: 'Edit item',
        icon: 'pi pi-pencil',
        command: () => this.pageMenu('Edit'),
      },
      {
        label: 'Settings',
        icon: 'pi pi-cog',
        command: () => this.pageMenu('Settings'),
      },
      {
        label: 'Export',
        icon: 'pi pi-download',
        items: [
          {
            label: 'Export PDF (without images)',
            icon: 'pi pi-file-pdf',
            command: () => this.pageMenu('DownloadPDF'),
          },
          {
            label: 'Export PDF (with images)',
            icon: 'pi pi-images',
            command: () => this.pageMenu('DownloadPDFWithImages'),
          },
          {
            label: 'Export to MS Word (.docx)',
            icon: 'pi pi-file-word',
            command: () => this.pageMenu('ExportDOCX'),
          },
          {
            label: 'Export to JSON file',
            icon: 'pi pi-file',
            command: () => this.pageMenu('ExportJSON'),
          },
        ],
      },
      {
        label: 'Print',
        icon: 'pi pi-print',
        command: () => this.pageMenu('Print'),
      },
      { separator: true },
      {
        label: 'DEBUG - show raw data',
        icon: 'pi pi-info-circle',
        command: () => this.pageMenu('ShowRawData'),
      },
      {
        label: this.showRawMarkup ? 'Switch to Rendered Markup View' : 'Switch to Raw Markup View',
        icon: this.showRawMarkup ? 'pi pi-eye' : 'pi pi-code',
        command: () => this.pageMenu('ToggleMarkupView'),
      },
      {
        label: 'New note',
        icon: 'pi pi-plus',
        command: () => this.pageMenu('New'),
      },
      {
        label: 'Create copy of note...',
        icon: 'pi pi-copy',
        command: () => this.pageMenu('CreateCopy'),
      },
      {
        label: 'Find Similar Notes',
        icon: 'pi pi-sparkles',
        command: () => this.pageMenu('FindSimilar'),
      },
      {
        label: 'Delete note',
        icon: 'pi pi-trash',
        command: () => this.pageMenu('Delete'),
      },
      { separator: true },
      {
        label: 'Close',
        icon: 'pi pi-times',
        command: () => this.pageMenu('Close'),
      },
    ];
  }

  private updateAiMenuItems(): void {
    const aiLabels = ['Find Similar Notes'];
    for (const item of this.menuItems) {
      if (item.label && aiLabels.includes(item.label)) {
        item.disabled = !this.isOnline;
        item.title = !this.isOnline ? 'AI features are only available in online mode' : undefined;
      }
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    // Skip shortcuts when the user is typing in any input, textarea, or
    // contenteditable element (e.g. the AI dialog question field, search boxes).
    const target = event.target as HTMLElement | null;
    if (
      target?.tagName === 'INPUT' ||
      target?.tagName === 'TEXTAREA' ||
      target?.tagName === 'SELECT' ||
      target?.isContentEditable
    ) {
      return;
    }

    if (event.key === 'e' || event.key === 'E') {
      event.preventDefault();
      this.pageMenu('Edit'); // same as the Edit button: the page keeps the open tab and reading position
    }
    if (event.key === 'n') {
      event.preventDefault();
      this.clickNew();
    }
    if (event.key === 'x') {
      event.preventDefault();
      this.exportToPDF();
    }
  }

  setTitle(newTitle: string) {
    this.titleService.setTitle(newTitle);
  }

  isLoading(): boolean {
    return this.loadingCounter > 0 && !this.note?._id;
  }

  toggleMobileMenu(menu: string) {
    if (menu === 'ViewMenu') {
      this.MobileViewMenuIsVisible = !this.MobileViewMenuIsVisible;
    }
  }

  goMobileMenu(selectedItem: string): void {
    console.log('goMobileMenu: ', selectedItem);
    if (selectedItem == 'Edit') {
      this.navigateToEdit(this.note._id);
    } else if (selectedItem == 'Settings') {
      this.router.navigate(['user-settings']);
    } else if (selectedItem == 'DownloadPDF') {
      this.printOrExportNote('export');
    } else if (selectedItem == 'Print') {
      this.printOrExportNote('print');
    } else if (selectedItem == 'home') {
      this.navigationService.clearHistory();
      this.navigateHome();
    }
  }

  pageMenu(selectedItem: string): void {
    console.log('pageMenu: ', selectedItem);
    if (selectedItem == 'Edit') {
      this.navigateToEdit(this.note._id);
    } else if (selectedItem == 'Settings') {
      this.router.navigate(['user-settings']);
    } else if (selectedItem == 'DownloadPDF') {
      this.printOrExportNote('export');
    } else if (selectedItem == 'DownloadPDFWithImages') {
      this.printOrExportNote('export', { includeAttachments: true });
    } else if (selectedItem == 'ExportDOCX') {
      this.exportToDocx();
    } else if (selectedItem == 'ExportJSON') {
      this.exportToJson();
    } else if (selectedItem == 'Print') {
      this.printOrExportNote('print');
    } else if (selectedItem == 'Home') {
      this.navigationService.clearHistory();
      this.navigateHome();
    } else if (selectedItem == 'New') {
      this.clickNew();
      //this.router.navigate(['home']);
    } else if (selectedItem == 'CreateCopy') {
      this.createCopyOfNote();
    } else if (selectedItem == 'FindSimilar') {
      this.findSimilarNotes();
    } else if (selectedItem == 'Delete') {
      this.deleteItem();
    } else if (selectedItem == 'Close') {
      this.navigationService.clearHistory();
      this.navigateHome();
    } else if (selectedItem == 'ShowRawData') {
      this.router.navigate(['debug-html', this.note._id]);
    } else if (selectedItem == 'ToggleMarkupView') {
      this.showRawMarkup = !this.showRawMarkup;
      this.initializePageMenu();
      this.snackbarService.showSuccessMessage(
        this.showRawMarkup ? 'Raw markup view enabled.' : 'Rendered markup view enabled.',
      );
    }
  }

  findSimilarNotes(): void {
    const noteId = this.note?._id;
    if (!noteId) {
      return;
    }

    this.similarNotesResults = [];
    this.similarNotesError = '';
    this.similarNotesLoading = true;
    this.similarNotesDialogVisible = true;

    this.data_service.findSimilarNotes(noteId).subscribe({
      next: (resp) => {
        this.similarNotesLoading = false;
        const raw: unknown = resp?.data;
        let results: SimilarNoteResult[];
        if (Array.isArray(raw)) {
          results = raw as SimilarNoteResult[];
        } else if (raw && typeof raw === 'object' && Array.isArray((raw as Record<string, unknown>)['results'])) {
          results = (raw as { results: SimilarNoteResult[] }).results;
        } else {
          results = [];
        }
        this.similarNotesResults = results.map((r) => {
          const id = String(r._id ?? r.id ?? '');
          const localNote = id ? this.data_service.allNotes.find((n) => n._id === id) : undefined;
          return { ...r, Status: localNote?.Status ?? r.Status };
        });
      },
      error: (err) => {
        this.similarNotesLoading = false;
        this.similarNotesError =
          err?.error?.error || err?.error?.message || err?.message || 'Failed to find similar notes.';
      },
    });
  }

  getSimilarNoteId(note: SimilarNoteResult): string {
    return String(note._id ?? note.id ?? '');
  }

  formatSimilarNoteDate(note: SimilarNoteResult): string {
    const raw = note.updated_at ?? note.modified_at ?? note.modifiedDate ?? note.last_modified ?? '';
    if (!raw) {
      return '-';
    }
    try {
      return new Date(String(raw)).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return String(raw);
    }
  }

  toggleFavorite(): void {
    const noteId = this.note?._id;
    if (!noteId) {
      this.snackbarService.showErrorMessage('No note selected.');
      return;
    }
    const isCurrentlyFavorite = this.favoriteNoteIds.includes(noteId);
    const updated = isCurrentlyFavorite
      ? this.favoriteNoteIds.filter((id) => id !== noteId)
      : [...this.favoriteNoteIds, noteId];

    // Offline-first: the local flip is the source of truth. userSettings.updateFavorites()
    // already writes it to IndexedDB (and skips the network call entirely when offline), so a
    // failed/unreachable remote push below must NOT undo this — it just means the push will be
    // retried on a future sync rather than the favorite silently reverting under the user.
    this.favoriteNoteIds = updated;
    this.isFavorite = !isCurrentlyFavorite;
    this.userSettings.setLocal({ favoriteNoteIds: updated });

    this.userSettings.updateFavorites(updated).subscribe({
      next: () => {
        const message = isCurrentlyFavorite ? 'Removed from favorites' : 'Added to favorites';
        this.snackbarService.showSuccessMessage(message);
      },
      error: (err) => {
        console.warn('Failed to sync favorite status to server (kept local change):', err);
      },
    });
  }

  private updateFavoriteState(): void {
    const noteId = this.note?._id;
    if (!noteId) {
      this.isFavorite = false;
      return;
    }
    this.isFavorite = this.favoriteNoteIds.includes(noteId);
  }

  private normalizeFavoriteNoteIds(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.filter((id) => typeof id === 'string');
  }

  getTagChipLabel(tag: Tag): string {
    return `${tag.name} · ${tag.tagType}`;
  }

  getTagMetaLine(tag: Tag): string {
    const parts = [tag.slug];
    const parentName = this.getParentTagName(tag);
    if (parentName) {
      parts.push(`Parent: ${parentName}`);
    }
    if (!tag.isActive) {
      parts.push('Inactive');
    }
    return parts.join(' · ');
  }

  getTagCardStyle(tag: Tag): Record<string, string> {
    const borderColor = tag.color || '#cbd5e1';
    return tag.isActive
      ? {
          borderColor,
          background: `linear-gradient(135deg, ${borderColor}18, rgba(255,255,255,0.96))`,
          cursor: 'pointer',
        }
      : {
          borderColor: '#cbd5e1',
          background: 'linear-gradient(135deg, rgba(226,232,240,0.55), rgba(255,255,255,0.96))',
          opacity: '0.82',
          cursor: 'pointer',
        };
  }

  filterByTag(tag: Tag): void {
    if (!tag?._id) {
      return;
    }

    const nextFilter: FilterParams = {
      ...this.filter,
      SearchString: tag.name,
      SearchType: 'Tags only',
      TagIds: [tag._id],
      TagScope: 'ANY',
    };

    this.filterService.setFilterParams(nextFilter);
    this.userSettings.setLocal({ homeView: 'List' });
    this.router.navigate(['/home'], {
      state: {
        filter: nextFilter,
        view: 'List',
      },
    });
  }

  private getParentTagName(tag: Tag): string {
    if (!tag.parentTagId) {
      return '';
    }

    return this.availableTags.find((candidate) => candidate._id === tag.parentTagId)?.name ?? tag.parentTagId;
  }

  private sortTagsByOrder(tags: Tag[]): Tag[] {
    return [...tags].sort((a, b) => {
      const orderA = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return a.name.localeCompare(b.name);
    });
  }

  clickNew(): void {
    console.log('🆕 clickNew() called');
    this.loadingCounter++; // optional, if you track loading states

    this.data_service.createNote().subscribe({
      next: (response: any) => {
        if (response.status === 'success' && response.data) {
          const newNote = response.data;
          console.log('✅ New note created locally:', newNote);

          // Navigate to edit view for this new note
          this.router.navigate(['/edit', newNote._id], {
            state: {
              filter: this.filter,
              homeState: this.homeState,
            },
          });
        } else {
          const msg = response.error?.message || 'Failed to create new note.';
          console.warn('⚠️ clickNew():', msg);
          this.alertService?.warn?.(msg, { life: 4000 });
        }

        this.loadingCounter--; // stop loading
      },

      error: (err) => {
        console.error('❌ clickNew(): unexpected error:', err);
        this.alertService?.error?.('An unexpected error occurred while creating a new note.', { life: 5000 });
        this.loadingCounter--;
      },
    });
  }

  createCopyOfNote(): void {
    if (!this.note) {
      this.snackbarService.showErrorMessage('Note is not available to copy.');
      return;
    }

    this.loadingCounter++;

    this.data_service.createNoteCopy(this.note).subscribe({
      next: (response: any) => {
        if (response.status === 'success' && response.data) {
          const newNote = response.data;
          console.log('✅ Copied note created locally:', newNote);

          this.router.navigate(['/edit', newNote._id], {
            state: {
              filter: this.filter,
              homeState: this.homeState,
            },
          });
        } else {
          const msg = response.error?.message || 'Failed to create note copy.';
          console.warn('⚠️ createCopyOfNote():', msg);
          this.alertService?.warn?.(msg, { life: 4000 });
        }

        this.loadingCounter--;
      },

      error: (err) => {
        console.error('❌ createCopyOfNote(): unexpected error:', err);
        this.alertService?.error?.('An unexpected error occurred while creating a note copy.', { life: 5000 });
        this.loadingCounter--;
      },
    });
  }

  onZoomFactorChange(type: 'system' | 'editor', event: any): void {
    if (type === 'editor') {
      console.log('Editor zoom factor changed to ', this.editorZoomFactor);
      this.userSettings.setEditorZoomFactor(this.editorZoomFactor);
    }
  }

  toggleZoomPanel(event: MouseEvent): void {
    event.stopPropagation();
    this.showZoomPanel = !this.showZoomPanel;
    if (this.showZoomPanel) {
      setTimeout(() => this.positionZoomPanel(), 0);
    }
  }

  private positionZoomPanel(): void {
    const triggerEl = this.triggerRef?.nativeElement;
    const panelEl = this.zoomPanelRef?.nativeElement;
    if (!triggerEl || !panelEl) return;

    const triggerRect = triggerEl.getBoundingClientRect();
    const panelRect = panelEl.getBoundingClientRect();
    const panelWidth = panelRect.width || 320;

    const viewportWidth = window.innerWidth;
    const margin = 12;
    let left = triggerRect.left + triggerRect.width / 2 - panelWidth / 2;
    left = Math.max(margin, Math.min(left, viewportWidth - panelWidth - margin));

    const top = triggerRect.top - panelRect.height - 12;

    this.zoomPanelStyle = {
      left: `${left}px`,
      top: `${Math.max(12, top)}px`,
    };
  }

  // persist after user releases the handle (optional but nice)
  persistEditorZoom(): void {
    this.userSettings.setEditorZoomFactor(this.editorZoomFactor);
  }

  toggleHeaderFields(): void {
    console.log('toggle header fields now =', !this.isHeaderVisible);
    this.isHeaderVisible = !this.isHeaderVisible;
  }

  toggleMetadataMore(): void {
    this.isMetadataMoreVisible = !this.isMetadataMoreVisible;
  }

  get isEventType(): boolean {
    return String(this.note?.NoteType || '').trim().toLowerCase() === 'event';
  }

  toggleDescription(): void {
    this.isDescriptionExpanded = !this.isDescriptionExpanded;
  }

  toggleAllCollapsibleSections(): void {
    if (!this.descriptionEditor?.editor) {
      console.warn('Description editor not available');
      return;
    }

    const newState = !this.areAllCollapsibleExpanded;
    this.areAllCollapsibleExpanded = newState;

    // Call the editor's method to toggle all sections
    this.descriptionEditor.toggleAllCollapsibleSections(newState);
  }

  toggleFileAttachments(): void {
    this.isFileAttachmentsExpanded = !this.isFileAttachmentsExpanded;
  }

  toggleTags(): void {
    this.isTagsExpanded = !this.isTagsExpanded;
  }

  toggleKeywords(): void {
    this.isKeywordsExpanded = !this.isKeywordsExpanded;
  }

  toggleRelatedNotes(): void {
    this.isRelatedNotesExpanded = !this.isRelatedNotesExpanded;
  }

  toggleComments(): void {
    this.isCommentsExpanded = !this.isCommentsExpanded;
  }

  getNoteTitleById(id: string): string {
    return this.data_service.allNotes?.find((n) => n._id === id)?.Title ?? '';
  }

  getNoteStatusById(id: string): string {
    return this.data_service.allNotes?.find((n) => n._id === id)?.Status ?? '';
  }

  getNoteLastModifiedById(id: string): string {
    return this.data_service.allNotes?.find((n) => n._id === id)?.last_modified ?? '';
  }

  get relatedNoteObjects(): { id: string; title: string; lastModified: string; status: string; aiSummary: AiSummary | null }[] {
    return this.relatedNotes.map((id) => {
      const note = this.data_service.allNotes?.find((n) => n._id === id);
      return {
        id,
        title: note?.Title || '',
        lastModified: note?.last_modified || '',
        status: note?.Status || '',
        aiSummary: note?.ai_summary ?? null,
      };
    });
  }

  getAiSummaryTooltip(summary: AiSummary | null): string {
    if (!summary) return '';
    switch (summary.summary_status) {
      case 'current':
      case 'stale':
        return summary.summary_one_sentence || '';
      case 'pending':
        return 'AI summary is being generated…';
      case 'failed':
        return summary.summary_error ? `Summary failed: ${summary.summary_error}` : 'AI summary failed.';
      default:
        return '';
    }
  }

  get aiSummary(): AiSummary | null {
    return this.note?.ai_summary ?? null;
  }

  aiSummaryStatusIcon(status: string): string {
    switch (status) {
      case 'current':
      case 'stale':
        return 'info';
      case 'pending':
        return 'pending';
      case 'failed':
        return 'error_outline';
      case 'disabled':
        return 'block';
      default:
        return 'help';
    }
  }

  aiSummaryStatusIconClass(status: string): Record<string, boolean> {
    return {
      'view-ai-summary-icon--stale': status === 'stale',
      'view-ai-summary-icon--pending': status === 'pending' || status === 'disabled',
      'view-ai-summary-icon--failed': status === 'failed',
    };
  }

  aiSummaryStatusLabel(status: string): string {
    switch (status) {
      case 'current':
        return 'Current';
      case 'stale':
        return 'Stale';
      case 'pending':
        return 'Pending';
      case 'failed':
        return 'Failed';
      case 'disabled':
        return 'Disabled';
      default:
        return status || 'Unknown';
    }
  }

  // Status metadata (tasks/frontend-note-status-metadata-requirements.md) — read-only here; missing
  // fields on older/offline notes read as actionRequired=false and priority=Normal.
  get noteActionRequired(): boolean {
    return isActionRequired(this.note);
  }

  get notePriority(): string {
    return normalizePriority(this.note?.priority);
  }

  get notePriorityIndicator() {
    return priorityIndicator(this.note?.priority);
  }

  /** "Action Required" for an unmigrated legacy "Next Action"; null for a current status. */
  get legacyStatusHint(): string | null {
    return isLegacyStatus(this.note?.Status) ? (legacyStatusHint(this.note?.Status) ?? 'no current equivalent') : null;
  }

  relatedNoteRowStyle(status: string): Record<string, string> {
    const isStrikethrough = isStruckStatus(status);
    const isMuted = isMutedStatus(status);
    return {
      'font-weight': isEmphasizedStatus(status) ? '700' : '400',
      'font-style': isMuted ? 'italic' : 'normal',
      color: isMuted ? 'var(--p-text-muted-color)' : 'var(--p-text-color)',
      'text-decoration': isStrikethrough ? 'line-through' : 'none',
    };
  }

  onClose() {
    this.navigateHome();
  }

  navigateToEdit(_id: string) {
    this.router.navigate(['/edit', _id], {
      state: {
        filter: this.filter,
        homeState: this.homeState,
      },
    });
  }

  private navigateHome(refresh = false): void {
    RichEditorComponent.clearPersistedCollapsibleStateForNote(this.note?._id);

    const state: any = {
      filter: this.filter,
      homeState: this.homeState,
    };
    if (refresh) {
      state.refresh = true;
    }
    this.router.navigate(['/home'], { state });
  }

  /**
   * Exports the current Note record to a PDF file.
   */
  exportToPDF(): void {
    const data = document.getElementById('noteContent');
    if (data) {
      html2canvas(data).then((canvas) => {
        const imgWidth = 210; // A4 width
        const pageHeight = 295; // A4 height
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const heightLeft = imgHeight;

        const contentDataURL = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4', true); // 'p' for portrait, 'mm' for millimeters, 'a4' for A4 size
        const position = 0;
        pdf.addImage(contentDataURL, 'PNG', 0, position, imgWidth, imgHeight, '', 'FAST');
        pdf.save('note.pdf');
      });
    }
  }

  //   printItem(): void {
  //     if (this.note) {
  //       this.printService.print(this.note);
  //     } else {
  //       console.error('Note is undefined.');
  //     }
  //   }

  // PDF TESTING - Jan 2025
  // Trigger PDF print
  printNote() {
    this.router.navigate(['export-print', this.note._id]);
  }

  async printOrExportNote(mode: string = 'export', options?: { includeAttachments?: boolean }): Promise<void> {
    console.log('printOrExportNote() - mode = ', mode);
    await this.noteActions.printOrExport(this.note, mode === 'print' ? 'print' : 'export', options);
  }

  async exportToDocx(): Promise<void> {
    if (!this.note) {
      this.snackbarService.showErrorMessage('Note is not available for export.');
      return;
    }
    this.viewDocxExportDialogVisible = true;
  }

  onViewDocxExportDialogCancel(): void {
    this.viewDocxExportDialogVisible = false;
  }

  async onViewDocxExportDialogSubmit(request: BulkActionRequest): Promise<void> {
    this.viewDocxExportDialogVisible = false;
    if (request.action !== 'export-docx') {
      return;
    }

    await this.exportCurrentNoteToDocxWithBulkOptions(request.params);
  }

  private async exportCurrentNoteToDocxWithBulkOptions(params?: BulkActionParams): Promise<void> {
    await this.noteActions.exportToDocx(this.note, params);
  }

  async exportToJson(): Promise<void> {
    await this.noteActions.exportToJson(this.note);
  }

  deleteItem() {
    this.noteActions.confirmAndDelete(
      this.note,
      () => {
        this.tempMessage = 'Moved to Trash';
        this.navigationService.clearHistory();
        this.navigateHome(true);
      },
      this.confirmationService,
    );
  }
}
